import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { Link, useLocation } from 'react-router-dom'

import { PageTitle } from '../../../../../_metronic/layout/core'
import { getMatchingPayoutTickets, getMatchingSummary } from '../../core/_requests'
import {
  PrepareMatchingPayoutTicketsToObject,
  MatchingPayoutActiveTicketsFilters,
  MatchingPayoutActiveTicketsRequestParams,
  MatchingSummary,
  PaginationState,
  getVisibleActivePayoutColumns,
} from '../../core/_models'
import ActivePayoutTicketsFilter from './ActivePayoutTicketsFilter'
import ActivePayoutTicketItem from './ActivePayoutTicketItem'
import { useAuth } from '../../../auth'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'

import '../../../../assets/css/pagination.css'
import { matchingActivePayoutsConfig } from './config'
import { Forbidden } from '../common/Forbidden'
import { formatSum } from '../../../../../utils/formatSum'
import CurrencySelect from '../common/CurrencySelect'
import { CURRENCY_STORAGE_KEY, DEFAULT_CURRENCY } from '../../core/currency'
import { usePayoutTicketActions, usePayoutTicketPermissions } from '../common/usePayoutTicketActions'

const { defaultFilters, defaultList, breadcrumbs, perPage, relevantStatusList } =
  matchingActivePayoutsConfig

const ActivePayoutTickets = () => {
  const { currentUser } = useAuth()
  const { search: locationSearch } = useLocation()

  // Токен тикета из адресной строки (?ticket=...) — для перехода со ссылки
  // «Уже переоплачивается» на строке депозита: подставляется в поисковый фильтр.
  const ticketFromUrl = useMemo(
    () => new URLSearchParams(locationSearch).get('ticket') || undefined,
    [locationSearch]
  )

  // Права и видимость. Переотправка КБ — отдельное право от canManageTickets: колонка действий
  // должна существовать и когда есть только оно (шапка/строка в синхроне).
  const { canAccess, canManageTickets, canCreateVirtualPayment, canResend } =
    usePayoutTicketPermissions(currentUser)
  const visibility = useMemo(() => getVisibleActivePayoutColumns(currentUser), [currentUser])
  const visibleColumns = useMemo(() => Object.keys(visibility).filter(Boolean), [visibility])

  // Состояния
  const [filters, setFilters] = useState<MatchingPayoutActiveTicketsFilters>(defaultFilters)
  const [pagination, setPagination] = useState<PaginationState>({ page: 1, perPage })
  const [tickets, setTickets] = useState(defaultList)
  const [isLoading, setIsLoading] = useState(false)
  const [countPages, setCountPages] = useState(0)
  const [fullCount, setFullCount] = useState(0)
  const [summaries, setSummaries] = useState<MatchingSummary[]>([])
  const [currency, setCurrencyState] = useState<string>(
    () => localStorage.getItem(CURRENCY_STORAGE_KEY) || DEFAULT_CURRENCY
  )
  const setCurrency = (c: string) => {
    localStorage.setItem(CURRENCY_STORAGE_KEY, c)
    setCurrencyState(c)
  }
  const [overpaymentCreatedToken, setOverpaymentCreatedToken] = useState<string | null>(null)

  // Единый «сейчас» для countdown-бейджей таймаута обработки: тикаем раз в 30с,
  // чтобы остаток на всех строках обновлялся без ручного рефреша списка. Один
  // таймер на таблицу — не заводим setInterval на каждую ячейку.
  const [nowMs, setNowMs] = useState<number>(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])

  // Суммарный остаток по активным выплатам — не зависит от пагинации/поиска,
  // но учитывает фильтры мерчанта и платёжного метода (тот же выбор, что и для списка).
  const loadSummary = useCallback(
    async (merchantTokenList?: string[], paymentMethodList?: string[]) => {
      if (!canAccess) return
      try {
        const params = {
          ...(merchantTokenList?.length ? { merchantTokenList } : {}),
          ...(paymentMethodList?.length ? { paymentMethodList } : {}),
        }
        const response = await getMatchingSummary(
          Object.keys(params).length ? params : undefined
        )
        setSummaries(response?.data?.success ? response.data.data ?? [] : [])
      } catch (error) {
        console.error('Ошибка при загрузке остатка активных выплат:', error)
        setSummaries([])
      }
    },
    [canAccess]
  )

  const currencies = useMemo(() => summaries.map((s) => s.currency), [summaries])

  // Keep the selection valid: if the stored currency isn't present in the
  // response, fall back to RUB when available, else the first currency.
  useEffect(() => {
    if (currencies.length && !currencies.includes(currency)) {
      setCurrencyState(currencies.includes(DEFAULT_CURRENCY) ? DEFAULT_CURRENCY : currencies[0])
    }
  }, [currencies, currency])

  const summary = useMemo(
    () => summaries.find((s) => s.currency === currency) ?? summaries[0],
    [summaries, currency]
  )

  const loadTicketList = useCallback(
    async (query: MatchingPayoutActiveTicketsRequestParams) => {
      if (!canAccess) return
      setIsLoading(true)
      try {
        const response = await getMatchingPayoutTickets(query)

        if (!response?.data?.success) {
          throw new Error(
            'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
          )
        }

        const tickets = PrepareMatchingPayoutTicketsToObject(response?.data?.list || [])
        const totalRows = response.data?.totalRows || 0
        const newCountPages = Math.ceil(totalRows / (query.perPage || perPage))

        // Если запрошенная страница вышла за пределы (например, после отмены
        // последнего тикета на последней странице) — откатываемся на последнюю.
        if (totalRows > 0 && query.page && query.page > newCountPages) {
          setPagination({ page: newCountPages, perPage: query.perPage || perPage })
          const fallbackResponse = await getMatchingPayoutTickets({
            ...query,
            page: newCountPages,
          })
          if (fallbackResponse?.data?.success) {
            setTickets(
              PrepareMatchingPayoutTicketsToObject(fallbackResponse.data?.list || []) || {}
            )
            setCountPages(newCountPages)
            setFullCount(fallbackResponse.data?.totalRows || 0)
            return
          }
        }

        setTickets(tickets || {})
        setCountPages(newCountPages)
        setFullCount(totalRows)
      } catch (error) {
        console.error('Ошибка при загрузке тикетов:', error)
        setTickets({})
        setCountPages(0)
        setFullCount(0)
      } finally {
        setIsLoading(false)
      }
    },
    [canAccess]
  )

  /**
   * Применение фильтров (подготовка запроса и отправка запроса на сервер)
   */
  const applyFilters = useCallback(
    (newFilters: MatchingPayoutActiveTicketsFilters, nextPagination?: PaginationState) => {
      if (!canAccess) return
      const filters = { ...defaultFilters, ...newFilters }
      setFilters({ ...filters })

      const effectivePagination: PaginationState = {
        page: nextPagination?.page || 1,
        perPage: nextPagination?.perPage || perPage,
      }
      setPagination(effectivePagination)

      // Формируем query только из поддерживаемых swagger-полей.
      // Пустые мульти-фильтры уходят как undefined — axios их не сериализует.
      const query: MatchingPayoutActiveTicketsRequestParams = {
        page: effectivePagination.page,
        perPage: effectivePagination.perPage,
        statusList: relevantStatusList,
        search: filters?.search || undefined,
        merchantTokenList: filters?.merchantTokenList?.length ? filters.merchantTokenList : undefined,
        paymentMethodList: filters?.paymentMethodList?.length ? filters.paymentMethodList : undefined,
      }

      loadTicketList(query)
      // Блок остатков пересчитываем под тот же выбор мерчанта и метода (CPAYMENT-1740).
      loadSummary(filters?.merchantTokenList, filters?.paymentMethodList)
    },
    [loadTicketList, loadSummary, canAccess]
  )

  const payoutActions = usePayoutTicketActions({
    enabled: canManageTickets,
    // Перечитываем список (applyFilters также обновит блок остатков под текущего мерчанта).
    onDone: () => applyFilters(filters, pagination),
  })

  // Перезапускается также при смене ?ticket в URL (клик по ссылке «В процессе
  // переоплаты» на том же роуте) — тогда список перефильтровывается по токену.
  useEffect(() => {
    applyFilters(
      { ...defaultFilters, ...(ticketFromUrl ? { search: ticketFromUrl } : {}) },
      { page: 1, perPage }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilters, ticketFromUrl])

  // Успешное создание переоплаты — показываем верхний alert с токеном
  // компенсационной выплаты и обновляем список.
  const handleOverpaymentCreated = useCallback(
    (compensationPayoutToken: string) => {
      setOverpaymentCreatedToken(compensationPayoutToken)
      applyFilters(filters, pagination)
    },
    [applyFilters, filters, pagination]
  )

  // Реквизит выплаты заменён — перечитываем список, чтобы строка показала новый канал.
  const handleRequisiteReplaced = useCallback(() => {
    applyFilters(filters, pagination)
  }, [applyFilters, filters, pagination])

  useEffect(() => {
    if (!overpaymentCreatedToken) return
    const timer = setTimeout(() => setOverpaymentCreatedToken(null), 10000)
    return () => clearTimeout(timer)
  }, [overpaymentCreatedToken])

  return (
    <>
      {payoutActions.modals}
      {overpaymentCreatedToken && (
        <div
          className='alert alert-success position-fixed top-0 start-50 translate-middle-x mt-5 shadow'
          style={{ zIndex: 1100 }}
          role='alert'
        >
          <div className='d-flex align-items-center justify-content-between gap-4'>
            <span>
              Переоплата создана. Компенсационная выплата:{' '}
              <Link to={`/matching/payout/active?ticket=${overpaymentCreatedToken}`}>
                {overpaymentCreatedToken}
              </Link>
            </span>
            <button
              type='button'
              className='btn-close'
              aria-label='Закрыть'
              onClick={() => setOverpaymentCreatedToken(null)}
            />
          </div>
        </div>
      )}
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={breadcrumbs}>Активные выплаты</PageTitle>
        <div className='card-header border-0 pt-5 pb-2 d-flex align-items-center justify-content-between'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>
              Мэтчинг &raquo; Активные выплаты
              {!!fullCount && <span className='text-muted fs-5'>&nbsp;({fullCount})</span>}
            </span>
          </h3>
          {canAccess && summary && (
            <div className='card-toolbar d-flex align-items-center gap-8'>
              <CurrencySelect currencies={currencies} value={currency} onChange={setCurrency} />
              <div className='d-flex flex-column align-items-end'>
                <span className='text-muted fs-7'>Остаток (с учётом процессинга)</span>
                <span className='fw-bold fs-3'>
                  {`${formatSum(summary.remainderIncludingProcessing)} ${summary.currency === 'RUB' ? '₽' : summary.currency}`}
                </span>
              </div>
              <div className='d-flex flex-column align-items-end'>
                <span className='text-muted fs-7'>Остаток (только завершённые)</span>
                <span className='fw-bold fs-3 text-muted'>
                  {`${formatSum(summary.remainderExcludingProcessing)} ${summary.currency === 'RUB' ? '₽' : summary.currency}`}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {!canAccess && (
        <div className='card mb-7'>
          <Forbidden />
        </div>
      )}

      {canAccess && (
        // key по токену из URL: переход по ссылке «Уже переоплачивается» на этом же роуте
        // пересоздаёт форму, чтобы в поле поиска оказался новый токен, а не предыдущий.
        <ActivePayoutTicketsFilter
          key={ticketFromUrl ?? 'default'}
          defaultFilters={defaultFilters}
          applyFilters={applyFilters}
          visibility={visibility}
          initialSearch={ticketFromUrl}
        />
      )}

      {!!canAccess && (
        <div className='card'>
          <div className='card-body py-3'>
            <Table>
              <TableHead>
                <tr>
                  <th />
                  {visibility.ticketToken && (
                    <th className='min-w-160px'>
                      ID тикета
                      <br />
                      <small>Токен UUID / ID тикета мерчанта</small>
                    </th>
                  )}
                  {visibility.user && <th className='align-top min-w-100px'>Клиент</th>}
                  {visibility.amount && <th className='min-w-100px align-top'>Cумма выплаты</th>}
                  {visibility.createdAt && (
                    <th className='min-w-150px align-top'>Время создания</th>
                  )}
                  {visibility.matchCount && (
                    <th className='min-w-125px align-top'>Кол-во матчей</th>
                  )}
                  {visibility.matchingRemainder && (
                    <th className='min-w-120px align-top'>Остаток матчинга</th>
                  )}
                  {visibility.depositRange && (
                    <th className='min-w-140px align-top'>Варианты депозитов</th>
                  )}
                  {visibility.merchant && <th className='min-w-120px align-top'>Мерчант</th>}
                  {visibility.status && <th className='min-w-100px align-top'>Статус тикета</th>}
                  {visibility.matchingStatus && <th className='min-w-120px align-top'>Статус мэтчинга</th>}
                  {visibility.requisites && <th className='min-w-180px align-top'>Реквизиты</th>}
                  {(canManageTickets || canResend) && (
                    <th className='min-w-150px text-end align-top'>
                      Завершить
                      <br />
                      <small>частично / полностью</small>
                    </th>
                  )}
                </tr>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <tr>
                    <td colSpan={visibleColumns.length}>Идет загрузка...</td>
                  </tr>
                ) : Object.keys(tickets).length > 0 ? (
                  Object.keys(tickets).map((ticketToken) => (
                    <ActivePayoutTicketItem
                      key={ticketToken}
                      ticket={tickets[ticketToken]}
                      nowMs={nowMs}
                      visibility={visibility}
                      visibleColumns={visibleColumns}
                      canManageTickets={canManageTickets}
                      canCreateVirtualPayment={canCreateVirtualPayment}
                      canResend={canResend}
                      virtualPaymentState={payoutActions.virtualPaymentState(ticketToken)}
                      onPartialComplete={payoutActions.openComplete}
                      onCancel={payoutActions.openCancel}
                      onOverpaymentCreated={handleOverpaymentCreated}
                      onRequisiteReplaced={handleRequisiteReplaced}
                      onOverpaymentHandedToCassa={handleRequisiteReplaced}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan={visibleColumns.length}>Данных нет.</td>
                  </tr>
                )}
              </TableBody>
            </Table>

            {countPages > 1 && (
              <div className='d-flex justify-content-center mt-5'>
                <ReactPaginate
                  breakLabel='...'
                  nextLabel='Следующая >'
                  onPageChange={(selectedItem) =>
                    applyFilters({ ...filters }, { page: selectedItem.selected + 1, perPage })
                  }
                  forcePage={Math.min(pagination.page, countPages) - 1}
                  pageRangeDisplayed={5}
                  pageCount={countPages}
                  previousLabel='< Предыдущая'
                  renderOnZeroPageCount={() => null}
                  className='pagination'
                  activeClassName='active'
                  disabledClassName='disabled'
                  nextClassName='page-item'
                  nextLinkClassName='page-link'
                  pageClassName='page-item'
                  pageLinkClassName='page-link'
                  previousClassName='page-item'
                  previousLinkClassName='page-link'
                  breakClassName='page-item'
                  breakLinkClassName='page-link'
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default ActivePayoutTickets
